import { createContext, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import {
  Edit2,
  Trash2,
  Key,
  Save,
  X,
  Power,
  UserCheck,
  UserX,
  User,
  FileText,
  MessageSquare,
  Bell,
  Eye,
  EyeOff,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../contexts/AuthContext'
import { employeeService, UpdateEmployeeData } from '../../services/employeeService'
import { noteService } from '../../services/noteService'
import { requestService } from '../../services/requestService'
import { positionService } from '../../services/positionService'
import { workCenterService } from '../../services/workCenterService'
import { Employee, EmployeeNote, LicenseRequest, Position, WorkCenter } from '../../types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { DetailLayout } from '../../components/ui/DetailLayout'
import { TabItem } from '../../components/ui/DetailTabs'

// Mapeo de códigos de turno a IDs de base de datos
// R2 employees don't have a shift record in the shifts table (shiftId=null)
const SHIFT_CODE_TO_ID: Record<string, string | null> = {
  'A': 'shift_a_001',
  'B': 'shift_b_001',
  'C': 'shift_c_001',
  'D': 'shift_d_001',
  'E': 'shift_e_001',
  'R3': 'shift_r3_001',
  'R2': null,
}

interface EmployeeContextType {
  employee: Employee | null
  notes: EmployeeNote[]
  requests: LicenseRequest[]
  positions: Position[]
  isLoading: boolean
  isAdmin: boolean
  isEditing: boolean
  isSaving: boolean
  formData: Partial<Employee>
  setIsEditing: (value: boolean) => void
  setFormData: (data: Partial<Employee>) => void
  handleSave: () => Promise<void>
  fetchEmployee: () => Promise<void>
  fetchNotes: () => Promise<void>
  fetchRequests: () => Promise<void>
}

const EmployeeContext = createContext<EmployeeContextType | null>(null)

export function useEmployeeContext() {
  const context = useContext(EmployeeContext)
  if (!context) {
    throw new Error('useEmployeeContext must be used within EmployeeDetailLayout')
  }
  return context
}

export function EmployeeDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'

  const [employee, setEmployee] = useState<Employee | null>(null)
  const [notes, setNotes] = useState<EmployeeNote[]>([])
  const [requests, setRequests] = useState<LicenseRequest[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showToggleConfirm, setShowToggleConfirm] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState<Partial<Employee>>({})
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([])

  useEffect(() => {
    if (id) {
      fetchEmployee()
      fetchNotes()
      fetchRequests()
      fetchPositions()
      fetchWorkCenters()
    }
  }, [id])

  const fetchEmployee = async () => {
    try {
      const data = await employeeService.getById(id!)
      setEmployee(data)
      setFormData(data)
    } catch (error) {
      toast.error('Error al cargar empleado')
      navigate('/employees')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchNotes = async () => {
    try {
      const data = await noteService.getByEmployeeId(id!)
      setNotes(data)
    } catch (error) {
      console.error('Error fetching notes:', error)
    }
  }

  const fetchRequests = async () => {
    try {
      const data = await requestService.getByEmployee(id!)
      setRequests(data)
    } catch (error) {
      console.error('Error fetching requests:', error)
    }
  }

  const fetchPositions = async () => {
    try {
      const data = await positionService.getAll()
      setPositions(data)
    } catch (error) {
      console.error('Error fetching positions:', error)
    }
  }

  const fetchWorkCenters = async () => {
    try {
      const data = await workCenterService.getActive()
      setWorkCenters(data)
    } catch (error) {
      console.error('Error fetching work centers:', error)
    }
  }

  const handleSave = async () => {
    if (!employee) return
    setIsSaving(true)
    try {
      // Convertir código de turno a ID si es necesario
      const shiftId = formData.shift ? SHIFT_CODE_TO_ID[formData.shift] : formData.shiftId

      // Si cambió el workCenterType, buscar el workCenterId correspondiente
      let workCenterId = formData.workCenterId || undefined
      if (formData.workCenterType && formData.workCenterType !== employee.workCenterType) {
        const matchingWc = workCenters.find(wc => wc.type === formData.workCenterType)
        if (matchingWc) {
          workCenterId = matchingWc.id
        }
      }

      const updateData: UpdateEmployeeData = {
        dni: formData.dni,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        positionId: formData.positionId || undefined,
        shiftId: shiftId || undefined,
        workCenterId: workCenterId,
        hasStop: formData.hasStop,
        daysAt5Shifts: formData.daysAt5Shifts,
        isActive: formData.isActive,
        isPresecadoCelulosa: formData.isPresecadoCelulosa,
        canRequestPP5R: formData.canRequestPP5R,
        daysUsedPP5R: formData.daysUsedPP5R,
      }
      await employeeService.update(employee.id, updateData)
      toast.success('Empleado actualizado')
      setIsEditing(false)
      fetchEmployee()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!employee || !newPassword) return
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres')
      return
    }
    setIsSaving(true)
    try {
      await employeeService.update(employee.id, { password: newPassword })
      toast.success('Contraseña actualizada')
      setShowPasswordModal(false)
      setNewPassword('')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar contraseña')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!employee) return
    try {
      await employeeService.delete(employee.id)
      toast.success('Empleado eliminado')
      navigate('/employees')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar')
    }
  }

  const handleToggleActive = async () => {
    if (!employee) return
    try {
      const updated = await employeeService.toggleActive(employee.id)
      toast.success(`Empleado ${updated.isActive ? 'activado' : 'desactivado'}`)
      setShowToggleConfirm(false)
      fetchEmployee()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar estado')
    }
  }

  const handleToggleClick = () => {
    if (employee?.isActive) {
      setShowToggleConfirm(true)
    } else {
      handleToggleActive()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Empleado no encontrado</p>
      </div>
    )
  }

  const tabs: TabItem[] = [
    { label: 'Información', path: '', icon: User, end: true },
    { label: 'Solicitudes', path: 'requests', icon: FileText, badge: requests.length || undefined },
    { label: 'Notas', path: 'notes', icon: MessageSquare, badge: notes.length || undefined },
    { label: 'Notificaciones', path: 'notifications', icon: Bell },
  ]

  const contextValue: EmployeeContextType = {
    employee,
    notes,
    requests,
    positions,
    isLoading,
    isAdmin,
    isEditing,
    isSaving,
    formData,
    setIsEditing,
    setFormData,
    handleSave,
    fetchEmployee,
    fetchNotes,
    fetchRequests,
  }

  return (
    <EmployeeContext.Provider value={contextValue}>
      <DetailLayout
        title={employee.fullName}
        subtitle={employee.position || undefined}
        backPath="/employees"
        basePath={`/employees/${id}`}
        tabs={tabs}
        avatar={
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-2xl">
            {employee.fullName.charAt(0).toUpperCase()}
          </div>
        }
        badges={
          <>
            {employee.hasUser ? (
              <span className="badge badge-success">
                <UserCheck className="h-3 w-3 mr-1" />
                Con acceso al sistema
              </span>
            ) : (
              <span className="badge bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                <UserX className="h-3 w-3 mr-1" />
                Sin acceso al sistema
              </span>
            )}
            {!employee.isActive && (
              <span className="badge badge-error">Inactivo</span>
            )}
          </>
        }
        actions={
          isAdmin && (
            <>
              {!isEditing ? (
                <>
                  <button
                    onClick={handleToggleClick}
                    className={`btn btn-sm flex items-center gap-1 ${
                      employee.isActive
                        ? 'btn-secondary'
                        : 'bg-success-50 text-success-700 hover:bg-success-100'
                    }`}
                  >
                    <Power className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{employee.isActive ? 'Desactivar' : 'Activar'}</span>
                  </button>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <Key className="h-4 w-4" />
                    <span className="hidden sm:inline">Cambiar Contraseña</span>
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Editar</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setFormData(employee)
                    }}
                    className="btn btn-secondary flex items-center gap-2"
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary flex items-center gap-2"
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                </>
              )}
            </>
          )
        }
      >
        <Outlet />
      </DetailLayout>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Cambiar Contraseña</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Introduce la nueva contraseña para {employee.fullName}
            </p>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input pr-10"
                placeholder="Nueva contraseña (min. 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPasswordModal(false)
                  setNewPassword('')
                  setShowPassword(false)
                }}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                className="btn btn-primary"
                disabled={isSaving || newPassword.length < 6}
              >
                {isSaving ? 'Guardando...' : 'Cambiar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Active Confirm */}
      <ConfirmDialog
        isOpen={showToggleConfirm}
        onClose={() => setShowToggleConfirm(false)}
        onConfirm={handleToggleActive}
        title="Desactivar Empleado"
        message={`¿Estás seguro de desactivar a ${employee.fullName}? No podrá iniciar sesión hasta que sea reactivado.`}
        confirmText="Desactivar"
        variant="danger"
      />

      {/* Delete Employee Confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar Empleado"
        message={`¿Estás seguro de eliminar a ${employee.fullName}? Se liberarán sus campos únicos (email, DNI, ID) para poder reutilizarlos. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </EmployeeContext.Provider>
  )
}
